import React from 'react';
import { Zap, Shield, Sparkles, TrendingUp } from 'lucide-react';
import { images } from '../mock';

export const HydrogenWaterExplained = () => {
  return (
    <section style={{
      background: '#2C3440',
      padding: '8rem 1.5rem 4rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle Mountain Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.1
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Main Header */}
        <div style={{ textAlign: 'center', marginBottom: '5rem', maxWidth: '900px', margin: '0 auto 5rem' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(92, 112, 128, 0.6)',
            color: '#00BFFF',
            padding: '0.75rem 1.5rem',
            borderRadius: '3rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginBottom: '2rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            border: '1px solid rgba(0, 191, 255, 0.3)',
            fontFamily: "'Open Sans', sans-serif"
          }}>
            The Science Behind Your Competitive Advantage
          </div>
          
          <h2 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            marginBottom: '1.5rem', 
            color: '#FFFFFF',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            fontStyle: 'italic'
          }}>
            What Makes Hydrogen Water Different?
          </h2>
          
          <p style={{ 
            fontFamily: "'Open Sans', sans-serif",
            marginBottom: '1rem', 
            color: '#E0E0E0',
            fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
            lineHeight: 1.6
          }}>
            Electrolyzed Reduced Hydrogen Water (ERW) isn't regular water. It's a cutting-edge hydration therapy that delivers molecular hydrogen—the most powerful antioxidant—directly to cells throughout the body.
          </p>
          
          <p style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            fontWeight: 600, 
            color: '#7EC0D6',
            fontSize: 'clamp(1.25rem, 2vw, 1.5rem)',
            fontStyle: 'italic'
          }}>
            This is the cellular building block technology that gives your program a measurable edge.
          </p>
        </div>

        {/* Visual Explanation Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '3rem',
          marginBottom: '5rem',
          alignItems: 'center'
        }} className="hydrogen-grid">
          {/* Image */}
          <div style={{
            borderRadius: '1rem',
            overflow: 'hidden',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)'
          }}>
            <img 
              src={images.solution}
              alt="Hydrogen water technology"
              style={{
                width: '100%',
                height: '450px',
                objectFit: 'cover'
              }}
            />
          </div>

          {/* Explanation */}
          <div>
            <h3 style={{ 
              fontFamily: "'Playfair Display', Georgia, serif",
              marginBottom: '1.5rem', 
              color: '#7EC0D6',
              fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
              fontWeight: 600,
              fontStyle: 'italic'
            }}>
              How Hydrogen Water Works at the Cellular Level
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{
                background: 'rgba(92, 112, 128, 0.4)',
                padding: '1.5rem',
                borderRadius: '0.75rem',
                borderLeft: '4px solid #00BFFF'
              }}>
                <h4 style={{ 
                  fontFamily: "'Open Sans', sans-serif",
                  marginBottom: '0.5rem', 
                  fontSize: '1.125rem', 
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  Molecular Hydrogen Penetrates Every Cell
                </h4>
                <p style={{ fontFamily: "'Open Sans', sans-serif", color: '#E0E0E0' }}>
                  Unlike regular water, hydrogen water contains dissolved H2 molecules small enough to cross cell membranes, reaching mitochondria where energy is produced.
                </p>
              </div>

              <div style={{
                background: 'rgba(92, 112, 128, 0.4)',
                padding: '1.5rem',
                borderRadius: '0.75rem',
                borderLeft: '4px solid #7EC0D6'
              }}>
                <h4 style={{ 
                  fontFamily: "'Open Sans', sans-serif",
                  marginBottom: '0.5rem', 
                  fontSize: '1.125rem', 
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  Neutralizes Oxidative Stress at the Source
                </h4>
                <p style={{ fontFamily: "'Open Sans', sans-serif", color: '#E0E0E0' }}>
                  Years of substance use creates massive oxidative damage. Hydrogen acts as a selective antioxidant, targeting only harmful free radicals while preserving beneficial ones.
                </p>
              </div>

              <div style={{
                background: 'rgba(92, 112, 128, 0.4)',
                padding: '1.5rem',
                borderRadius: '0.75rem',
                borderLeft: '4px solid #5C7080'
              }}>
                <h4 style={{ 
                  fontFamily: "'Open Sans', sans-serif",
                  marginBottom: '0.5rem', 
                  fontSize: '1.125rem', 
                  color: '#FFFFFF',
                  fontWeight: 600
                }}>
                  Supports Every System in the Body
                </h4>
                <p style={{ fontFamily: "'Open Sans', sans-serif", color: '#E0E0E0' }}>
                  From brain function to immune response, cellular health is the foundation. When cells function optimally, everything else improves—energy, mood, cognition, and physical recovery.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Why This Is Your Competitive Edge */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(0, 191, 255, 0.2) 0%, rgba(92, 112, 128, 0.4) 100%)',
          borderRadius: '1rem',
          padding: '4rem 2rem',
          color: 'white',
          textAlign: 'center',
          border: '1px solid rgba(0, 191, 255, 0.3)'
        }}>
          <h3 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            marginBottom: '2rem', 
            color: 'white',
            fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
            fontWeight: 700,
            fontStyle: 'italic'
          }}>
            Why Hydrogen Water Gives Your Program an Edge
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            <div>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(0, 191, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Zap size={32} color="#00BFFF" />
              </div>
              <h4 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.75rem', color: 'white', fontSize: '1.125rem', fontWeight: 600 }}>Cutting-Edge Technology</h4>
              <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.95rem', lineHeight: '1.6', color: '#E0E0E0' }}>
                Most facilities don't offer cellular-level therapy. This sets you apart immediately.
              </p>
            </div>

            <div>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(0, 191, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Shield size={32} color="#00BFFF" />
              </div>
              <h4 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.75rem', color: 'white', fontSize: '1.125rem', fontWeight: 600 }}>Science-Backed Results</h4>
              <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.95rem', lineHeight: '1.6', color: '#E0E0E0' }}>
                Over 1,000 peer-reviewed studies validate hydrogen water's therapeutic effects.
              </p>
            </div>

            <div>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(0, 191, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Sparkles size={32} color="#00BFFF" />
              </div>
              <h4 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.75rem', color: 'white', fontSize: '1.125rem', fontWeight: 600 }}>Visible Client Improvements</h4>
              <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.95rem', lineHeight: '1.6', color: '#E0E0E0' }}>
                Clients feel the difference in energy, clarity, and physical comfort within days.
              </p>
            </div>

            <div>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(0, 191, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <TrendingUp size={32} color="#00BFFF" />
              </div>
              <h4 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.75rem', color: 'white', fontSize: '1.125rem', fontWeight: 600 }}>Marketing Advantage</h4>
              <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.95rem', lineHeight: '1.6', color: '#E0E0E0' }}>
                "Cellular Recovery Therapy" is a powerful differentiator for referral sources and families.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 1024px) {
          .hydrogen-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};
